import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { MatterRead } from './schemas'

export type MattersRecord = Partial<{ [matter_id: string]: MatterRead }>

export enum MatterDialogType {
  Create = 'create',
  Edit = 'edit',
  Delete = 'delete',
  AcceptInvite = 'acceptInvite',
  ManageMembers = 'manage-members',
}

interface MatterDialogState {
  dialogType: MatterDialogType | null
  matterId: string | null
  /** When true, after a successful create, set the new matter as the active matter. */
  switchToMatterOnCreate: boolean
  /** When true, after a successful create, navigate to the matter overview (`/dashboard/m/:id`). */
  navigateToMatterOnCreate: boolean
  /** Which surface opened the dialog. Plain string (not an analytics enum) to keep this store decoupled
   * from analytics — see README.md ("Matter jurisdiction nudges") in src/analytics for why. */
  source: string | null
}

const initialDialogState: MatterDialogState = {
  dialogType: null,
  matterId: null,
  switchToMatterOnCreate: false,
  navigateToMatterOnCreate: false,
  source: null,
}

export type MattersState = {
  matters: MattersRecord
  initialLoadComplete: boolean
  lastRefresh: number | null
  loading: boolean
  error: boolean
  /* Monotonic id of the latest matter-list fetch; a response tagged with an older id is dropped so a slow poll can't overwrite a fresher forced refresh. */
  requestId: number
  dialog: MatterDialogState
  activeMatterId: string | null
}

const initialState: MattersState = {
  matters: {},
  initialLoadComplete: false,
  lastRefresh: null,
  loading: false,
  error: false,
  requestId: 0,
  dialog: initialDialogState,
  activeMatterId: null,
}

/**
 * Matters Slice
 *
 * Manages state related to matters.
 *
 * A Matter represents a user-owned workspace that organizes conversations,
 * files, and client information for a specific case or project.
 */
export const mattersSlice = createSlice({
  name: 'mattersState',
  initialState,
  reducers: {
    // ============== Listener Middleware Triggers ============== >
    refreshMatterList: (_state, _action: PayloadAction<{ forceRefresh: boolean }>) => {
      // Handled by listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    // ============== Matter Actions ============== >
    // Claim a new latest matter-list fetch: bump the request id and enter the loading state.
    // The listener drops any response whose id is no longer the latest.
    beginMatterListRefresh: (state, action: PayloadAction<number>) => {
      state.requestId = action.payload
      state.loading = true
      state.error = false
    },

    // Set loading state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },

    // Set error state
    setError: (state, action: PayloadAction<boolean>) => {
      state.error = action.payload
    },

    // Set initial load complete
    setInitialLoadComplete: (state, action: PayloadAction<boolean>) => {
      state.initialLoadComplete = action.payload
    },

    // Set last refresh
    setLastRefresh: (state, action: PayloadAction<number | null>) => {
      state.lastRefresh = action.payload
    },

    // Replace the full list of matters while preserving existing unchanged entries
    setMatters: (state, action: PayloadAction<MatterRead[]>) => {
      // Build a new matters record
      const newMatters: Record<string, MatterRead> = {}

      action.payload.forEach((matter) => {
        const existingMatter = state.matters[matter.id]

        // Reuse the existing object (preserves referential equality, avoids re-renders) only when
        // nothing the table renders has changed. `updated_at` alone is insufficient: membership and
        // invite changes (member_count, viewer_role, invited_at, accepted_at) don't bump updated_at,
        // so compare those too — otherwise a member add/remove or invite-accept never reaches the UI.
        const unchanged =
          existingMatter &&
          existingMatter.updated_at === matter.updated_at &&
          existingMatter.member_count === matter.member_count &&
          existingMatter.viewer_role === matter.viewer_role &&
          existingMatter.invited_at === matter.invited_at &&
          existingMatter.accepted_at === matter.accepted_at

        newMatters[matter.id] = unchanged ? existingMatter : matter
      })

      // Perform a single replacement of all matters
      state.matters = newMatters
    },

    // Add or update a single matter
    upsertMatter(state, action: PayloadAction<MatterRead>) {
      const matterId = action.payload.id
      state.matters[matterId] = action.payload
    },

    // Remove a matter by id
    removeMatter(state, action: PayloadAction<{ matterId: string }>) {
      const { matterId } = action.payload
      delete state.matters[matterId]
    },

    // Set the currently active matter ID (or clear it with null)
    setActiveMatterId: (state, action: PayloadAction<{ matterId: string | null; source?: string | null }>) => {
      const { matterId } = action.payload
      const currentActiveMatterId = state.activeMatterId

      if (currentActiveMatterId !== matterId) {
        state.activeMatterId = matterId
      }
    },

    // ============== Matter Dialog Actions ============== >
    // Open the create matter dialog
    openDialog: (
      state,
      action: PayloadAction<{
        dialogType: MatterDialogType
        matterId?: string
        switchToMatterOnCreate?: boolean
        navigateToMatterOnCreate?: boolean
        source?: string
      }>
    ) => {
      state.dialog = {
        dialogType: action.payload.dialogType,
        matterId: action.payload.matterId ?? null,
        switchToMatterOnCreate: action.payload.switchToMatterOnCreate ?? false,
        navigateToMatterOnCreate: action.payload.navigateToMatterOnCreate ?? false,
        source: action.payload.source ?? null,
      }
    },

    // Close the dialog
    closeDialog: (state) => {
      state.dialog = initialDialogState
    },
  },
})

// Actions
export const MattersActions = mattersSlice.actions

export default mattersSlice.reducer
